import { motion } from 'framer-motion';
import { CheckCircle, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

const Packages = () => {
  const packages = [
    {
      name: 'Starter Plan',
      price: '₹9,999',
      period: '/month',
      target: 'Small Businesses & New Brands',
      platforms: 'Instagram, Facebook',
      featured: false,
      features: [
        '8 Static Posts per month',
        '3 Reels',
        'Basic Template Designs',
        'Hashtag Optimization',
        'Story Updates',
        'Monthly Report',
      ],
    },
    {
      name: 'Growth Plan',
      price: '₹16,999',
      period: '/month',
      target: 'Growing Businesses',
      platforms: 'Instagram, Facebook',
      featured: true,
      features: [
        '13 Posts per month',
        '5 Reels',
        '1 Meta Ad Campaign',
        'Custom Designs',
        'GMB Setup + Weekly Posts',
        'Story Updates + DM Management',
        'Keyword Strategy',
        'Bi-weekly Analytics Report',
      ],
    },
    {
      name: 'Professional Plan',
      price: '₹24,999',
      period: '/month',
      target: 'Established Businesses',
      platforms: 'Instagram, Facebook, LinkedIn',
      featured: false,
      features: [
        '20 Premium Posts per month',
        '10 Reels with Voiceover',
        '2 Meta Ad Campaigns',
        'GMB Full Management',
        'LinkedIn Professional Updates',
        'Advanced SEO & Geo Tagging',
        'Priority Support',
        'Weekly Performance Reports',
      ],
    },
    {
      name: 'Enterprise Plan',
      price: '₹36,000',
      period: '/month',
      target: 'Brands Focused on Scaling',
      platforms: 'Instagram, Facebook, LinkedIn, YouTube',
      featured: false,
      features: [
        '30 Premium Posts + 15 Reels',
        'YouTube Shorts Management',
        '4 Multi-Platform Ad Campaigns',
        'Complete GMB Optimization',
        'Email Marketing (2 Campaigns/Month)',
        'Influencer Collaboration Support',
        'Dedicated Account Manager',
        'Real-time Analytics Dashboard',
        'Monthly Strategy Session',
      ],
    },
  ];

  return (
    <div className="min-h-screen pt-32 pb-20" data-testid="packages-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-20"
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-6 gradient-text" data-testid="packages-title">
            Choose Your Package
          </h1>
          <p className="text-gray-400 text-lg max-w-3xl mx-auto leading-relaxed">
            Flexible pricing plans designed to match your business goals and budget
          </p>
        </motion.div>

        {/* Packages Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {packages.map((pkg, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
              whileHover={{ y: -10 }}
              className={`relative rounded-2xl p-8 transition-all ${
                pkg.featured
                  ? 'bg-gradient-to-b from-gray-900 to-black border-2 border-[#FFD700] shadow-[0_0_40px_rgba(255,215,0,0.2)]'
                  : 'glass hover:border-white/20'
              }`}
              data-testid={`package-${index}`}
            >
              {pkg.featured && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-to-r from-[#FFD700] to-[#FDB931] text-black px-6 py-2 rounded-full font-semibold text-sm flex items-center space-x-1">
                    <Star size={16} className="fill-black" />
                    <span>Best Seller</span>
                  </div>
                </div>
              )}

              <div className="mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">{pkg.name}</h3>
                <p className="text-sm text-gray-400 mb-4">{pkg.target}</p>
                <div className="flex items-baseline mb-2">
                  <span className="text-5xl font-bold gold-gradient-text">{pkg.price}</span>
                  <span className="text-gray-400 ml-2">{pkg.period}</span>
                </div>
                <p className="text-sm text-[#3B82F6]">{pkg.platforms}</p>
              </div>

              <ul className="space-y-3 mb-8">
                {pkg.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start space-x-2">
                    <CheckCircle size={18} className="text-[#3B82F6] mt-0.5 flex-shrink-0" />
                    <span className="text-gray-300 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/contact"
                data-testid={`get-started-btn-${index}`}
                className={`block w-full text-center py-4 rounded-full font-semibold transition-all ${
                  pkg.featured
                    ? 'bg-gradient-to-r from-[#FFD700] to-[#FDB931] text-black hover:shadow-[0_0_30px_rgba(255,215,0,0.5)] hover:-translate-y-1'
                    : 'bg-gradient-to-r from-[#3B82F6] to-[#2563EB] text-white hover:shadow-[0_0_20px_rgba(59,130,246,0.5)]'
                }`}
              >
                Get Started
              </Link>
            </motion.div>
          ))}
        </div>

        {/* Payment Info */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="glass rounded-2xl p-8 text-center"
        >
          <h3 className="text-2xl font-bold mb-4 text-white">Payment Structure</h3>
          <p className="text-gray-400 mb-4">
            50% Advance + 50% after mid-month review
          </p>
          <p className="text-sm text-gray-500">
            3 working days onboarding timeline • Monthly progress reviews included
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Packages;